import React from 'react';
import { Copy, ExternalLink, Check, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Modal } from '../../../components';
import { inputCls, fieldLabelCls, fmtDate } from './shared';

/**
 * The dialog both candidate links open into.
 *
 * It shows the URL even when the email went out, and that is the point: email
 * to a personal address fails quietly and often, and a manager who can read the
 * link off the screen can send it themselves through whatever the candidate
 * actually answers. The banner says plainly which of the two happened, because
 * "sent" and "created but not delivered" need different actions from the
 * manager and a single green tick would hide the difference.
 */
const LinkModal = ({ result, onClose, candidate, title }) => {
  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select the link and copy it manually');
    }
  };

  return (
    <Modal
      isOpen={Boolean(result)}
      onClose={onClose}
      title={title}
      maxWidth="max-w-xl"
      footer={<div className="flex justify-end"><Button onClick={onClose}>Done</Button></div>}
    >
      <div className="space-y-4">
        <p className="text-[13px] text-[#475569] dark:text-gray-300">
          For {candidate.name}
          {candidate.email ? ` · ${candidate.email}` : ''}
        </p>

        <div className={`p-4 rounded-xl flex items-start gap-2.5 ${
          result?.emailed
            ? 'bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/40'
            : 'bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/40'
        }`}>
          {result?.emailed
            ? <Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            : <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />}
          <p className="text-[13px] text-[#475569] dark:text-gray-300">
            {result?.emailed
              ? 'The form has been emailed to the candidate. You can also share this link directly.'
              : 'The link was created but the email could NOT be sent. Copy it below and share it with the candidate yourself.'}
          </p>
        </div>

        <div>
          <label className={fieldLabelCls} htmlFor="candidate-form-url">Form link</label>
          <input
            id="candidate-form-url"
            readOnly
            className={`${inputCls} font-mono text-[12.5px]`}
            value={result?.url || ''}
            onFocus={(event) => event.target.select()}
          />
          {result?.expires_at && (
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
              Expires {fmtDate(result.expires_at)}. Sending again replaces it.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2.5">
          <Button
            variant="primary"
            leftIcon={<ExternalLink className="w-4 h-4" />}
            onClick={() => window.open(result.url, '_blank', 'noopener')}
          >
            Open candidate form
          </Button>
          <Button variant="secondary" leftIcon={<Copy className="w-4 h-4" />} onClick={() => copy(result.url)}>
            Copy link
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default LinkModal;
